import { localStorageStore } from "ra-core";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { VoiceLanguageCode } from "../voice-agent/useVoiceAgentLanguage";
import { VOICE_LANGUAGES } from "../voice-agent/useVoiceAgentLanguage";
import {
  BC_ENVIRONMENT_STORE_KEY,
  DEFAULT_BC_ENVIRONMENT,
  isBcEnvironmentCode,
  type BcEnvironmentCode,
} from "../misc/bcEnvironment";

/** Same store instance used by CRM so setItem notifies useStore subscribers. */
export const crmPreferencesStore = localStorageStore(undefined, "CRM");

export const UI_LOCALES = ["en", "fr", "da", "sv", "fi"] as const;
export type UiLocale = (typeof UI_LOCALES)[number];

const VOICE_CODES = VOICE_LANGUAGES.map((l) => l.code) as VoiceLanguageCode[];

const CURRENT_SALE_CACHE_KEY = "RaStore.auth.current_sale";

export type SalePreferences = {
  locale?: string | null;
  voice_language?: string | null;
  bc_environment?: string | null;
};

function isUiLocale(value: unknown): value is UiLocale {
  return (
    typeof value === "string" &&
    (UI_LOCALES as readonly string[]).includes(value)
  );
}

function isVoiceLanguage(value: unknown): value is VoiceLanguageCode {
  return (
    typeof value === "string" &&
    (VOICE_CODES as readonly string[]).includes(value)
  );
}

function getLocalStorage(): Storage | null {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  return null;
}

export function patchCurrentSaleCache(patch: SalePreferences) {
  const storage = getLocalStorage();
  const cached = storage?.getItem(CURRENT_SALE_CACHE_KEY);
  if (cached == null) return;
  try {
    const parsed = JSON.parse(cached) as Record<string, unknown>;
    storage?.setItem(
      CURRENT_SALE_CACHE_KEY,
      JSON.stringify({ ...parsed, ...patch }),
    );
  } catch {
    // ignore corrupt cache
  }
}

export function applyPreferencesToStore(
  locale?: string | null,
  voiceLanguage?: string | null,
  bcEnvironment?: string | null,
) {
  if (isUiLocale(locale)) {
    crmPreferencesStore.setItem("locale", locale);
  }
  if (isVoiceLanguage(voiceLanguage)) {
    crmPreferencesStore.setItem("crm.voiceLanguage", voiceLanguage);
  }
  if (isBcEnvironmentCode(bcEnvironment)) {
    crmPreferencesStore.setItem(BC_ENVIRONMENT_STORE_KEY, bcEnvironment);
  }
}

export async function saveUiPreferences(partial: {
  locale?: string | null;
  voiceLanguage?: string | null;
  bcEnvironment?: string | null;
}): Promise<{
  locale: string | null;
  voice_language: string | null;
  bc_environment: string | null;
} | null> {
  const locale =
    partial.locale !== undefined
      ? partial.locale
      : (crmPreferencesStore.getItem<string>("locale") ?? null);
  const voiceLanguage =
    partial.voiceLanguage !== undefined
      ? partial.voiceLanguage
      : (crmPreferencesStore.getItem<string>("crm.voiceLanguage") ?? null);
  const bcEnvironment =
    partial.bcEnvironment !== undefined
      ? partial.bcEnvironment
      : (crmPreferencesStore.getItem<string>(BC_ENVIRONMENT_STORE_KEY) ??
        DEFAULT_BC_ENVIRONMENT);

  applyPreferencesToStore(locale, voiceLanguage, bcEnvironment);

  const { data, error } = await getSupabaseClient().rpc(
    "set_my_ui_preferences",
    {
      p_locale: isUiLocale(locale) ? locale : null,
      p_voice_language: isVoiceLanguage(voiceLanguage) ? voiceLanguage : null,
      p_bc_environment: isBcEnvironmentCode(bcEnvironment)
        ? bcEnvironment
        : null,
    },
  );

  if (error) {
    throw error;
  }

  const row = Array.isArray(data) ? data[0] : data;
  const saved: SalePreferences = {
    locale: row?.locale ?? (isUiLocale(locale) ? locale : null),
    voice_language:
      row?.voice_language ??
      (isVoiceLanguage(voiceLanguage) ? voiceLanguage : null),
    bc_environment:
      row?.bc_environment ??
      (isBcEnvironmentCode(bcEnvironment) ? bcEnvironment : null),
  };
  patchCurrentSaleCache(saved);
  return {
    locale: saved.locale ?? null,
    voice_language: saved.voice_language ?? null,
    bc_environment: saved.bc_environment ?? null,
  };
}

let hydratedForUserKey: string | null = null;

export function resetPreferencesHydration() {
  hydratedForUserKey = null;
}

/**
 * Apply DB preferences to the RA store. If DB is empty but localStorage has
 * values, migrate them once via RPC so existing users keep their choice.
 */
export async function hydratePreferencesFromSale(
  sale: SalePreferences & { id?: number | string } | null | undefined,
  userKey?: string | null,
) {
  if (sale == null) return;

  const key = userKey ?? (sale.id != null ? String(sale.id) : null);
  if (key != null && hydratedForUserKey === key) {
    return;
  }
  if (key != null) {
    hydratedForUserKey = key;
  }

  const hasDbLocale = isUiLocale(sale.locale);
  const hasDbVoice = isVoiceLanguage(sale.voice_language);
  const hasDbBcEnv = isBcEnvironmentCode(sale.bc_environment);

  if (hasDbLocale || hasDbVoice || hasDbBcEnv) {
    applyPreferencesToStore(
      hasDbLocale ? sale.locale : null,
      hasDbVoice ? sale.voice_language : null,
      hasDbBcEnv ? sale.bc_environment : DEFAULT_BC_ENVIRONMENT,
    );
    // If BC env missing in DB but we have locale/voice, still ensure default BC env is saved when migrating
    if (!hasDbBcEnv) {
      try {
        await saveUiPreferences({
          bcEnvironment: DEFAULT_BC_ENVIRONMENT as BcEnvironmentCode,
        });
      } catch {
        crmPreferencesStore.setItem(
          BC_ENVIRONMENT_STORE_KEY,
          DEFAULT_BC_ENVIRONMENT,
        );
      }
    }
    return;
  }

  const localLocale = crmPreferencesStore.getItem<string>("locale");
  const localVoice = crmPreferencesStore.getItem<string>("crm.voiceLanguage");
  const localBcEnv = crmPreferencesStore.getItem<string>(
    BC_ENVIRONMENT_STORE_KEY,
  );
  if (
    !isUiLocale(localLocale) &&
    !isVoiceLanguage(localVoice) &&
    !isBcEnvironmentCode(localBcEnv)
  ) {
    crmPreferencesStore.setItem(
      BC_ENVIRONMENT_STORE_KEY,
      DEFAULT_BC_ENVIRONMENT,
    );
    return;
  }

  try {
    await saveUiPreferences({
      locale: isUiLocale(localLocale) ? localLocale : null,
      voiceLanguage: isVoiceLanguage(localVoice) ? localVoice : null,
      bcEnvironment: isBcEnvironmentCode(localBcEnv)
        ? localBcEnv
        : DEFAULT_BC_ENVIRONMENT,
    });
  } catch {
    // Demo/FakeRest or offline: keep local values only
  }
}

/**
 * Always fetch prefs from DB (bypass sale identity cache) and hydrate.
 */
export async function fetchAndHydrateUiPreferences(userKey?: string | null) {
  if (userKey != null && hydratedForUserKey === userKey) {
    return;
  }

  const supabase = getSupabaseClient();
  const { data: sessionData, error: sessionError } =
    await supabase.auth.getSession();
  if (sessionError || sessionData?.session?.user == null) {
    return;
  }

  const { data, error } = await supabase
    .from("sales")
    .select("id, locale, voice_language, bc_environment")
    .match({ user_id: sessionData.session.user.id })
    .single();

  if (error || data == null) {
    return;
  }

  patchCurrentSaleCache({
    locale: data.locale,
    voice_language: data.voice_language,
    bc_environment: data.bc_environment,
  });
  await hydratePreferencesFromSale(
    data,
    userKey ?? (data.id != null ? String(data.id) : null),
  );
}
